import React, { FC, useState } from 'react';
import { PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import { XMarkIcon as CloseIcon } from '@heroicons/react/24/solid';
import Button from 'components/commercetools-ui/atoms/button';
import Dropdown from 'components/commercetools-ui/atoms/dropdown';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import DiscountBadge from 'components/commercetools-ui/molecules/badge/DiscountBadge';
import Breadcrumb from 'components/commercetools-ui/molecules/breadcrumb';
import Gallery from 'components/commercetools-ui/organisms/gallery';
import ShareIcon from 'components/icons/share-icon';
import { useAddToCartOverlay } from 'context/add-to-cart-overlay';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import { getDiscount } from 'helpers/utils/getDiscount';
import { ShippingMethod } from 'types/entity/cart';
import { Category } from 'types/entity/category';
import { Variant } from 'types/entity/product';
import { LineItem, Wishlist } from 'types/entity/wishlist';
import { AddItemResponse } from 'frontastic/hooks/useCart/types';
import Bundles from './components/bundles';
import ProductInformation from './components/product-information';
import ProductPrice from './components/product-price';
import ProductReferenceToggle from './components/product-reference-toggle';
import useTrack from './hooks/useTrack';
import { Attribute, DeliveryOption, UIProduct } from './types';

export interface ProductDetailsProps {
  product: UIProduct;
  variant: Variant;
  url?: string;
  category?: Category;
  wishlist?: Wishlist;
  shippingMethods?: ShippingMethod[];
  inModalVersion?: boolean;
  productAttributes?: Attribute[];
  onChangeVariant: (sku: string) => void;
  setIsOpen?: (value: boolean) => void;
  removeLineItem?: (item: LineItem) => Promise<void>;
  addToWishlist?: (lineItem: LineItem, count: number) => Promise<void>;
  onAddToCart?: (
    variant: Variant,
    quantity: number,
    subscriptions?: Variant[],
    references?: Variant[],
  ) => Promise<AddItemResponse>;
}

const ProductDetails: FC<ProductDetailsProps> = ({
  product,
  variant,
  category,
  url,
  wishlist,
  shippingMethods,
  inModalVersion,
  productAttributes,
  onChangeVariant,
  setIsOpen,
  removeLineItem,
  addToWishlist,
  onAddToCart,
}) => {
  const { formatMessage } = useFormat({ name: 'cart' });
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const [selectedReferences, setSelectedReferences] = useState<Variant[]>(product.variants);
  const [quantity, setQuantity] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const [showQtyExceeded, setShowQtyExceeded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState<Variant[]>(
    Array(product?.subscriptions?.length).fill(null),
  );

  const { trackAddToCart } = useTrack({ product, inModalVersion });

  const { show, fetchRelatedProducts } = useAddToCartOverlay();

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement> | number) => {
    const value = typeof e === 'number' ? e : Number(e.target.value.replace(/^0+/, '')) || 0;
    if (typeof e !== 'number') e.target.value = value.toString();
    setShowQtyExceeded(value > 99);
    const quantityToSet = Math.min(99, Math.max(1, value));
    setQuantity(quantityToSet);
  };

  const handleSelectReference = (selectedVariants?: Variant[]) => {
    setSelectedReferences(selectedVariants || []);
  };

  const handleAddToCart = async () => {
    setLoading(true);
    const intendedValue: number = quantity + (showQtyExceeded ? 1 : 0);
    onAddToCart?.(variant, quantity, selectedSubscriptions, selectedReferences).then((res: AddItemResponse) => {
      setLoading(false);

      setAdded(true);
      setShowQtyExceeded(false);
      setTimeout(() => {
        setAdded(false);
        setQuantity(1);
      }, 1000);

      show(product, variant, res.addQuantity, intendedValue);
    });
    trackAddToCart();
  };

  const handleSubscriptionSelect = (index: number, sku: string) => {
    const selectedVariant = !sku ? null : product?.subscriptions?.[index].variants.find((v: Variant) => v.sku === sku);
    setSelectedSubscriptions(
      selectedSubscriptions.map((_, i) => {
        if (i === index) {
          return selectedVariant;
        }
        return _;
      }) as Variant[],
    );
  };

  const handleShare = () => {
    const url = window.location.href;
    const title = document.title;
    try {
      navigator.clipboard.writeText(url);
      if (navigator?.share) {
        navigator.share({ title, url });
      }
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      console.error('Unable to Share');
    }
  };

  const defaultVariant = product?.variants[0];

  const { hasDiscount, discountPercentage } = getDiscount(defaultVariant);

  const wrapperClassName = inModalVersion
    ? 'md:grid grid-cols-12 pt-70 pb-35 px-20 md:pr-36'
    : 'px-16 pb-32 pt-20 md:mt-64 md:grid md:grid-cols-12 md:items-start md:px-24 md:pt-24 xl:mx-auto xl:mt-0 xl:max-w-[1200px] xl:px-0';

  const galleryContainerClassName = useClassNames([
    inModalVersion ? 'col-span-6' : 'md:col-span-4',
    'md:pr-26 lg:pr-60 h-full relative',
  ]);

  const informationContainerClassName = useClassNames([
    inModalVersion ? 'col-span-6' : 'md:col-span-8',
    'mt-24 md:mt-0',
  ]);
  // const productAttributesList = productAttributes?.map((a: { name: string }) => a.name) || [];

  const productDetailAttributesList = ['practice-area', 'ISBN', 'jurisdiction'];

  const isUKjurisdiction =
    product?.variants?.some(
      (item) => item?.attributes?.jurisdiction === 'United Kingdom' || item?.attributes?.jurisdiction === 'UK',
    ) && product.productTypeKey !== 'ebook';
  return (
    <div className={wrapperClassName}>
      {category && !inModalVersion && (
        <Breadcrumb Separator="/" className="col-span-12 mb-24 hidden w-fit lg:block">
          <Link
            key={category.categoryId}
            link={category._url}
            className="text-14 text-primary-black underline-offset-4 hover:text-blue-900 hover:underline"
          >
            {category.name}
          </Link>

          <Typography key={product.slug} className="cursor-default text-14 text-neutral-500">
            {product.name}
          </Typography>
        </Breadcrumb>
      )}

      <div className={galleryContainerClassName}>
        <Gallery images={variant.images ?? []} inModalVersion={inModalVersion} />
        <div className="absolute left-0 top-0 z-10">
          <div className="w-full text-center">
            {hasDiscount && <DiscountBadge discountPercentage={discountPercentage} />}
          </div>
        </div>
        <div className="absolute right-0 top-0 z-10 md:pr-26 lg:pr-64">
          <div className="group w-32 cursor-pointer text-center" onClick={handleShare}>
            <ShareIcon
              className="group-hover:fill-orange-600 group-focus:fill-orange-600 group-active:fill-orange-600"
              fill=""
            />
          </div>
        </div>
      </div>

      <div className={informationContainerClassName}>
        <ProductInformation
          product={product}
          variant={variant}
          category={category}
          onChangeVariant={onChangeVariant}
          inModalVersion={inModalVersion}
          wishlist={wishlist}
          removeLineItem={removeLineItem}
          addToWishlist={addToWishlist}
        />

        {!!product?.subscriptions?.length && (
          <Bundles
            bundles={product.subscriptions}
            onBundleSelect={handleSubscriptionSelect}
            selectedBundles={selectedSubscriptions}
          />
        )}

        <ProductReferenceToggle
          product={product}
          selectedReferences={selectedReferences}
          onSelect={handleSelectReference}
        />
        <div className="pt-20">
          <Typography className="pb-8 text-lg font-bold leading-relaxed text-neutral-700">Quantity</Typography>
          <div className="flex items-center gap-12">
            <button
              aria-label="Decrease quantity"
              className="flex items-center justify-center rounded-3xl border border-black p-8 hover:bg-neutral-100 disabled:opacity-30"
              disabled={quantity <= 1}
              onClick={() => handleQuantityChange(Math.max(1, quantity - 1))}
            >
              <MinusIcon width={17.5} strokeWidth={2} className="size-10" />
            </button>
            <input
              aria-label="Quantity"
              type="number"
              min="1"
              max="99"
              value={quantity}
              onChange={handleQuantityChange}
              className="rounded border-y border-gray-600 px-12 py-5 text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            <button
              aria-label="increase quantity"
              className="flex items-center justify-center rounded-3xl border border-black p-8 hover:bg-neutral-100 disabled:opacity-30"
              onClick={() => handleQuantityChange(Math.min(99, quantity + 1))}
              disabled={quantity >= 99}
            >
              <PlusIcon width={17.5} strokeWidth={2} className="size-10" />
            </button>
          </div>
          {/* {!variant.isOnStock && (
            <div className="pt-20">
              <p className="font-medium text-red-500">
                {formatProductMessage({ id: 'outOfStock', defaultMessage: 'Out of stock' })}
              </p>
            </div>
          )} */}
        </div>
        {showQtyExceeded && (
          <div className="flex gap-8 pt-20">
            <Typography className="text-12 font-semibold text-red-600 md:text-14">
              {formatProductMessage({
                id: 'quantity.exceeded',
                defaultMessage: 'For bulk orders of 100 copies or more, call us.',
              })}
            </Typography>
          </div>
        )}
        <div className="flex gap-8 pt-20">
          <ProductPrice className="product-price-ele" variants={selectedReferences} inModalVersion={inModalVersion} />
        </div>
        <div className="flex gap-8 pt-20">
          <Button
            className="inline-flex w-auto items-center justify-center gap-1 !rounded-full bg-orange-600 py-8 text-center text-lg font-bold leading-snug text-white"
            variant="primary"
            onClick={handleAddToCart}
            loading={loading}
            added={added}
            // disabled={!variant.isOnStock}
          >
            {formatMessage({ id: 'cart.add', defaultMessage: 'Add to cart' })}
          </Button>
        </div>

        {variant?.attributes?.['delivery_options'] && (
          <>
            <div className="flex flex-col pt-20">
              <Typography className="h-full justify-center pb-10 text-start text-14 leading-loose text-secondary-black">
                {formatMessage({ id: 'delivery.options', defaultMessage: 'Delivery options' })}
              </Typography>
              <Dropdown
                className="h-full rounded-sm"
                value={quantity.toString()}
                defaultValue={variant?.attributes?.['delivery_options']?.find(
                  (deliveryOption: DeliveryOption) => deliveryOption.label.toLowerCase() === 'one time delivery',
                )}
                selectOptions={variant?.attributes?.['delivery_options']
                  ?.map((deliveryOption: DeliveryOption) => ({
                    name: deliveryOption.label,
                    value: deliveryOption.key,
                  }))
                  .reverse()}
                onChange={(e) => handleQuantityChange(Number(e.target.value))}
              />
            </div>
          </>
        )}
        {/* {!inModalVersion && <ShippingSection shippingMethods={shippingMethods} />} */}

        {inModalVersion && (
          <Link
            link={url}
            className="mx-auto mt-28 block w-fit border-b border-transparent text-center text-14 leading-loose text-secondary-black hover:border-secondary-black"
            onClick={() => setIsOpen?.(false)}
          >
            {formatMessage({ id: 'more.details', defaultMessage: 'More details' })}
          </Link>
        )}

        {!inModalVersion && (
          <div className="grid pb-2 md:col-span-7 md:mb-20 md:pr-20 lg:col-span-8 lg:pr-52">
            <Typography className="border-b border-b-black py-20 text-lg font-bold leading-relaxed text-neutral-700">
              Product details
            </Typography>
            {isUKjurisdiction && (
              <div className="grid-cols-3 pt-20">
                <Typography className="text-orange-600">
                  Dispatched from overseas. Estimated delivery 3-6 weeks.
                </Typography>
              </div>
            )}
            <div className="grid grid-cols-2 gap-x-10 gap-y-6 pt-20 md:pt-14">
              {productDetailAttributesList.map((a: string) => {
                if (variant?.attributes?.[a]) {
                  return (
                    <div key={a} className="flex flex-row gap-4">
                      <Typography className="font-semibold capitalize">{`${a.replace(/-/g, ' ')}: `}</Typography>
                      <Typography className="text-neutral-700">
                        {typeof variant?.attributes[a] !== 'string'
                          ? JSON.stringify(variant?.attributes[a])
                          : variant?.attributes[a]}
                      </Typography>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        )}
        <div className="grid md:col-span-7 md:mb-50 md:pr-20 lg:col-span-8 lg:pr-52">
          {product?.description && (
            <div className="relative">
              <div
                className={`overflow-hidden transition-all duration-300 ${showDescription ? 'max-h-none' : 'line-clamp-[3]'}`}
              >
                <div
                  className="text-sm leading-[1.5em] text-neutral-700"
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />
              </div>
              <button
                onClick={() => setShowDescription(!showDescription)}
                className="mt-12 text-sm font-medium text-blue-600 hover:text-blue-800"
              >
                {showDescription ? 'Show less' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      </div>
      {copied && (
        <div className="fixed left-0 top-0 z-20 flex size-full translate-y-1/3 justify-center overflow-hidden md:translate-y-1/4">
          <div className="relative flex h-fit w-full max-w-[90vw] justify-center rounded-md border-2 border-orange-600 bg-orange-200/90 p-12 text-center drop-shadow md:max-w-350">
            <Typography className="text-base font-bold text-orange-600">
              {formatProductMessage({ id: 'copied', defaultMessage: 'Copied' })}
            </Typography>
            <button type="reset" onClick={() => setCopied(false)} className="absolute right-10 top-10">
              <CloseIcon className="w-24 fill-primary-black" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetails;
